import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import useClickAwayListener from '../../../../hooks/use-click-away-listener';
import Portal from '../../../general-components/portal';
import { ColorResult } from '@uiw/react-color';
import PickerBody from './parts/picker-body';

interface Props {
	open: boolean;
	onChange: (c: ColorResult) => void;
	onClose?: () => void;
	openHexAtStart?: string | undefined;
	portalId?: string;
	pickerWidth?: number;
	pickerClassname?: string;
	customPosition?: {
		left?: number;
		right?: number;
		top?: number;
		bottom?: number;
	}
}

type AnimationState = 'closed' | 'open' | 'closing';

export default function ColorPicker(props: Props) {
	const { 
		open, 
		onChange, 
		onClose = () => {},
		openHexAtStart = '#151515', 
		portalId,
		pickerWidth = 270,
		pickerClassname = ''
	} = props;

	const [animationState, setAnimationState] = useState<AnimationState>('closed');
	const colorPickerWrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (open) {
			setAnimationState('open');
		} else if (animationState === 'open') {
			setAnimationState('closing');
		}
	}, [open, animationState]);

	const handleAnimationEnd = () => {
		if (animationState === 'closing') {
			setAnimationState('closed');
		}
	};

	useClickAwayListener({
		ref: colorPickerWrapperRef,
		onClickAway: () => null,
		// don't use onMouseDownClickAway or it will flash between pickers
		// because it will take a moment for the next picker to render
		onMousedownClickAway: () => onClose(),
		onEscape: () => onClose(),
		open,
	});

	const addProp = (obj: any, prop: string, value: number | undefined) => {
		if (typeof value === 'number') {
			obj[prop] = `${value}px`
		}
	}

	const position = {} as any;
	if (props?.customPosition) {
		addProp(position, 'top', props?.customPosition?.top)
		addProp(position, 'bottom', props?.customPosition?.bottom)
		addProp(position, 'right', props?.customPosition?.right)
		addProp(position, 'left', props?.customPosition?.left)
	}

	// Only render when not in closed state
	if (animationState === 'closed') {
		return null;
	}

	const pickerElement = (
		<div
			ref={colorPickerWrapperRef}
			style={{
				'--width-of-picker': `${pickerWidth}px`,
				...position
			} as CSSProperties}
			onAnimationEnd={handleAnimationEnd}
			className={classNames(
				"color-picker-portal-child",
				{ 
					[openHexAtStart]: true,
					"color-picker--open": animationState === 'open',
					"color-picker--closing": animationState === 'closing' && !open,
				},
			)}
		>
			<PickerBody
				onChangeComplete={onChange}
				colorHex={openHexAtStart}
				pickerClassname={pickerClassname}
				width={pickerWidth}
			/>
		</div>
	);

	// Render without Portal if portalId is empty string or undefined
	if (!portalId) {
		return pickerElement;
	}

	return (
		<Portal targetId={portalId}>
			{pickerElement}
		</Portal>
	);
}